import * as React from 'react';
import { useEffect, useState } from 'react';
import { OnProgressProps } from 'react-player/types/base';
import * as S from './Video.styled';
import { AlertSnackbar } from '../AlertSnackbar';
import { VideoPropsType } from './Video.types';
import { Controls } from './Controls';

export const Video = (props: VideoPropsType) => {
  const {
    src,
    playbackRate,
    onSetProgressPlayed,
    playerRef,
    onSetPlayedSeconds,
    playing,
    onSeekChange,
    progressPlayed = 0,
    allowedProgressRegions = [],
    isSuccessTargetRegions,
    refreshPreSignedUrl,
  } = props;

  const [isOpenError, setIsOpenError] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isMute, setIsMute] = useState(false);
  const [volume, setVolume] = useState<number>(1);
  const [retryingUrlRefresh, setRetryingUrlRefresh] = useState(false);
  const [retryUrlLastPlayed, setRetryUrlLastPlayed] = useState(0);

  useEffect(() => {
    if (isSuccessTargetRegions && isReady && allowedProgressRegions.length) {
      const first = allowedProgressRegions[0];
      const last = allowedProgressRegions[allowedProgressRegions.length - 1];
      if (progressPlayed < first.start && first.start > 0) {
        onSeekChange(first.start, false);
      }
      allowedProgressRegions.forEach(({ start }, index) => {
        if (
          index > 0 &&
          progressPlayed < start &&
          progressPlayed >= allowedProgressRegions[index - 1].end
        ) {
          onSeekChange(start, true);
        } else if (progressPlayed > last.end) {
          onSeekChange(last.end, false);
        }
      });
    }
  }, [allowedProgressRegions, progressPlayed, onSeekChange, isReady, isSuccessTargetRegions]);

  const handleProgress = (progress: OnProgressProps) => {
    const { played: progressPlayed, playedSeconds: progressPlayedSeconds } = progress;
    // fixes bug of react-player lib that reduces the progress value
    const roundedProgress = Number(progressPlayed.toFixed(6));
    onSetProgressPlayed(roundedProgress);
    onSetPlayedSeconds(progressPlayedSeconds);
  };

  return (
    <S.Root>
      <AlertSnackbar
        text={'Something went wrong with the audit video'}
        open={isOpenError}
        handleClose={() => setIsOpenError(false)}
      />
      <S.Video
        ref={playerRef}
        width='100%'
        height='100%'
        url={src || ''}
        volume={volume}
        muted={isMute}
        playbackRate={playbackRate}
        onProgress={handleProgress}
        progressInterval={100}
        playing={playing}
        onBuffer={() => setBuffering(true)}
        onBufferEnd={() => setBuffering(false)}
        onReady={() => {
          if (retryingUrlRefresh) {
            setRetryingUrlRefresh(false);
            playerRef.current?.seekTo(retryUrlLastPlayed);
          }
          setIsOpenError(false);
          setIsDisabled(false);
          setIsReady(true);
        }}
        onError={(error) => {
          console.log('Processing error', error.target.error.code);
          setIsOpenError(true);
          setIsDisabled(true);
          setIsReady(false);
          if (error.target.error.code === 2) {
            setRetryingUrlRefresh(true);
            setRetryUrlLastPlayed(playerRef.current?.getCurrentTime() || 0);
            refreshPreSignedUrl();
          }
        }}
        config={{
          file: {
            forceVideo: true,
          },
        }}
      />
      <Controls
        {...props}
        buffering={buffering}
        isDisabled={isDisabled}
        muted={isMute}
        onMuted={setIsMute}
        volume={volume}
        handleChangeVolume={setVolume}
      />
    </S.Root>
  );
};
